import axios from 'axios';
import { getApiBaseUrl } from '../config/apiConfig';

const getApiUrl = () => getApiBaseUrl();

export interface TwilioSettings {
  id?: number;
  account_sid: string;
  auth_token: string;
  is_active: boolean;
  
  // SMS
  sms_enabled: boolean;
  sms_phone_number: string;
  sms_webhook_url: string;
  sms_status_callback_url: string;
  
  // WhatsApp
  whatsapp_enabled: boolean;
  whatsapp_phone_number: string;
  whatsapp_webhook_url: string;
  whatsapp_status_callback_url: string;
  
  // Voice
  voice_enabled: boolean;
  voice_phone_number: string;
  voice_webhook_url: string;
  voice_status_callback_url: string;
  voice_recording_enabled: boolean;
  voice_recording_webhook_url: string;
  
  // Email
  email_enabled: boolean;
  sendgrid_api_key: string;
  email_from_address: string;
  email_from_name: string;
  email_domain: string;
  email_inbound_webhook_url: string;
  email_tracking_enabled: boolean;
  
  // Video
  video_enabled: boolean;
  video_api_key: string;
  video_api_secret: string;
  video_max_participants: number;
  video_recording_enabled: boolean;
  
  // Webhook
  webhook_secret: string;
  webhook_signature_validation: boolean;
  
  // Usage
  usage_limits?: {
    sms?: number;
    whatsapp?: number;
    voice?: number;
    email?: number;
    video?: number;
  };
  current_usage?: {
    sms?: number;
    whatsapp?: number;
    voice?: number;
    email?: number;
    video?: number;
  };
  last_sync_at?: string;
  
  created_at?: string;
  updated_at?: string;
}

export interface ServiceStatus {
  configured: boolean;
  is_active?: boolean;
  services: {
    sms: {
      enabled: boolean;
      ready: boolean;
      phone_number: string;
    };
    whatsapp: {
      enabled: boolean;
      ready: boolean;
      phone_number: string;
    };
    voice: {
      enabled: boolean;
      ready: boolean;
      phone_number: string;
      recording_enabled: boolean;
    };
    email: {
      enabled: boolean;
      ready: boolean;
      from_address: string;
      tracking_enabled: boolean;
    };
    video: {
      enabled: boolean;
      ready: boolean;
      max_participants: number;
      recording_enabled: boolean;
    };
  };
}

export interface UsageStats {
  current_usage: {
    [key: string]: number;
  };
  limits: {
    [key: string]: number;
  };
  last_sync_at: string | null;
}

class TwilioSettingsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get current Twilio settings.
   */
  async getSettings(): Promise<TwilioSettings> {
    const response = await axios.get(`${getApiUrl()}/admin/twilio`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Update Twilio settings.
   */
  async updateSettings(settings: Partial<TwilioSettings>): Promise<TwilioSettings> {
    const response = await axios.put(`${getApiUrl()}/admin/twilio`, settings, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Test connection with provided credentials.
   */
  async testConnection(accountSid: string, authToken: string, service: string): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    const response = await axios.post(
      `${getApiUrl()}/admin/twilio/test-connection`,
      { account_sid: accountSid, auth_token: authToken, service },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Get status of all Twilio services.
   */
  async getStatus(): Promise<ServiceStatus> {
    const response = await axios.get(`${getApiUrl()}/admin/twilio/status`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Get usage statistics.
   */
  async getUsage(): Promise<UsageStats> {
    const response = await axios.get(`${getApiUrl()}/admin/twilio/usage`, {
      headers: this.getAuthHeaders(),
    });
    return response.data.data;
  }

  /**
   * Reset usage statistics.
   */
  async resetUsage(): Promise<void> {
    await axios.post(
      `${getApiUrl()}/admin/twilio/usage/reset`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }
}

export default new TwilioSettingsService();
